import React, { useState, useRef, useEffect } from 'react';

/**
 * LazyImage - A component that shows a blur placeholder while loading
 * Features:
 * - Native lazy loading
 * - Blur-up effect on load
 * - Smooth fade-in transition
 * - Explicit dimensions to prevent CLS
 */
const LazyImage = ({ 
  src, 
  alt, 
  className = '', 
  placeholderColor = '#e5e7eb',
  blurAmount = 20,
  width,
  height,
  aspectRatio = '16/9',
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px', threshold: 0.1 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div 
      ref={imgRef}
      className={`relative overflow-hidden ${className}`}
      style={{ 
        backgroundColor: placeholderColor,
        aspectRatio: aspectRatio,
        width: width || '100%',
        height: height || 'auto'
      }}
    >
      {/* Blur placeholder */}
      {!isLoaded && (
        <div 
          className="absolute inset-0 animate-pulse"
          style={{ 
            backgroundColor: placeholderColor,
            filter: `blur(${blurAmount}px)`,
          }}
        />
      )}
      
      {/* Actual image */}
      {isInView && (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          width={width}
          height={height}
          onLoad={() => setIsLoaded(true)}
          className={`w-full h-full object-cover transition-opacity duration-500 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          {...props}
        />
      )}
    </div>
  );
};

/**
 * LazyImageWithPreview - Shows a tiny blurred preview that transitions to full image
 * Includes aspect-ratio for CLS prevention
 */
export const LazyImageWithPreview = ({ 
  src, 
  alt, 
  className = '',
  previewSrc = null,
  width,
  height,
  aspectRatio = '4/3',
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const containerRef = useRef(null);

  // Generate a tiny placeholder URL using a color gradient
  const generatePlaceholder = () => {
    if (previewSrc) return previewSrc;
    return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 300'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' stop-color='%23f3f4f6'/%3E%3Cstop offset='100%25' stop-color='%23e5e7eb'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect fill='url(%23g)' width='400' height='300'/%3E%3C/svg%3E`;
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '150px', threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div 
      ref={containerRef}
      className={`relative overflow-hidden ${className}`}
      style={{ 
        aspectRatio: aspectRatio,
        width: width || '100%',
        height: height || 'auto'
      }}
    >
      {/* Blurred placeholder */}
      <img
        src={generatePlaceholder()}
        alt=""
        aria-hidden="true"
        width={width}
        height={height}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
          isLoaded ? 'opacity-0' : 'opacity-100'
        }`}
        style={{ filter: 'blur(20px)', transform: 'scale(1.1)' }}
      />
      
      {/* Main image */}
      {isInView && (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          width={width}
          height={height}
          onLoad={() => setIsLoaded(true)}
          className={`relative w-full h-full object-cover transition-opacity duration-500 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          {...props}
        />
      )}
    </div>
  );
};

/**
 * LogoImage - Optimized for small logo images with fade-in
 * Includes explicit dimensions to prevent CLS
 */
export const LogoImage = ({ 
  src, 
  alt, 
  className = '',
  width = 120,
  height = 60,
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <div 
      className={`relative ${className}`}
      style={{ width, height, minWidth: width, minHeight: height }}
    >
      {!isLoaded && (
        <div className="absolute inset-0 bg-gray-100 animate-pulse rounded" />
      )}
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        width={width}
        height={height}
        onLoad={() => setIsLoaded(true)}
        className={`transition-opacity duration-300 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ width, height, objectFit: 'contain' }}
        {...props}
      />
    </div>
  );
};

export default LazyImage;
